import { AppContext } from "App";

import { WorkflowEditorConnector } from "@multiweb/reactConnectors/WorkflowEditorConnector";
import { IAttrValue } from "@multiweb/types/contracts";
import { Event, StatefulEvent } from "Global/Base/Events";
import { UUID } from "Global/Base/IDs/UUID";
import { StandardOperationRights } from "Global/Base/Rights/StandardOperationRights";
import { StringUtils } from "Global/Base/Utils/StringUtils";
import { EOWValidateException } from "OW.Remoting/Basic/OWExceptions";
import { AttrValueReader } from "OW.Remoting/Remoting/AttrValueReader";
import { OWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { Localization } from "OW.Remoting/Remoting/Localization";
import { OWManager } from "OW.Remoting/Remoting/OWManager";
import { ErrorHandler } from "UI/Errors";
import { SaveResultData } from "UI/UI.Controls/DataSource/EditableDBDataSource";
import { WorkflowEditorMode } from "UI/UI.React/workflowEditor";
import { WorkflowManager } from "UI/UI.React/workflowEditor/WorkflowManager";
import { workflowToXml, xmlToWorkflow } from "UI/UI.React/workflowEditor/workflowParser";
import { ComponentTreeType } from "UI/UI/LogicalTree/Definitions";
import { RightsManagerType } from "UI/UI/Rights/Definitions";
import { StringBuilder } from "../DataBuilders/StringBuilder";
import { IMenu } from "../FormUI/FormUI";
import { MessageWindow } from "../MessageWindow";
import { UIManager } from "../UIManager";
import { FrameSelChanged, HideButtons, IActualObjectChangedEventArgs } from "../XGControls/Frames";
import { IMasterableFrame, IValidatableMasterableFrame } from "../XGControls/Frames/FrameInterfaces";
import { FrameSharedMethods } from "../XGControls/Frames/FrameSharedMethods";
import { IEditableFrame } from "../XGControls/Frames/IEditableFrame";
import { MasterableFormControl } from "../XGControls/Frames/MasterableFormControl";
import { IFrame, IFrameCtxMenuData } from "../XGControls/IFrame";
import { SharedMethods } from "../XGControls/SharedMethods";
import { ValidationManager } from "../XGControls/ValidationManager";
import { FormControlParams } from "./ControlInterfaces";

const CONST_SWORKFLOW_CLASS_NAME = "SWorkflow";
const CONST_DEFINITION_CHANGED_ATTR_NAME = "definice_zmenena";
const CONST_SELECTID_PARAM_NAME = "select-id";
export const CONST_STATUS_VIEW_ENABLED = "statusViewEnabled=true";

export class WorkflowEditor extends MasterableFormControl implements IValidatableMasterableFrame, IEditableFrame {
    private _workflowConnector: WorkflowEditorConnector;
    private _wfEditorHostKey: string;
    private _setValuesMap: Map<string, string>;
    private _openedViaUrl: boolean = false;

    public isMainFrame = true;
    public frameName: string = null;
    public actualObject: OWObject = new OWObject(this.frameContext);
    public renderStandardRowActions = false;
    public actualObjectValueChanged: Event<IFrame, OWObject> = new Event<IFrame, OWObject>();
    public actualObjectSaved: Event<IMasterableFrame, OWObject> = new Event<IMasterableFrame, OWObject>();
    public isActive = true;
    public hideButtons: HideButtons;
    public refreshObjectAfterSetActive = false;
    public refreshListAfterSetActive = false;
    public frameIsVisible: Event<IFrame, void> = new Event<IFrame, void>();
    public contextMenuOpening: Event<IFrame, IFrameCtxMenuData> = new Event<IFrame, IFrameCtxMenuData>();
    public actualObjectChanged: Event<IFrame, IActualObjectChangedEventArgs> = new Event<IFrame, IActualObjectChangedEventArgs>();
    public contextActionsReady: Event<IFrame, string> = new Event<IFrame, string>();
    public initialized: StatefulEvent<IFrame, void> = new StatefulEvent<IFrame, void>();
    public initializationStarted = false;
    public isInitialized = false;
    public hideToolBar: boolean;
    public menu: IMenu;
    public masterObject: OWObject = new OWObject(this.frameContext);
    public masterFrameName: string = null;
    public masterLinkToAttr: string = null;
    public masterAttr: string = null;
    public readonly uuid: string;
    public suppressSaveDialog = false;
    public isSetActualObjectRunning?: boolean;
    public interactive = true;
    public canBeMasterFrame: boolean = true;
    public selectionChanged: Event<IMasterableFrame, FrameSelChanged> = new Event<WorkflowEditor, FrameSelChanged>();
    public permanentInvisible?: boolean;
    public hasSelectedRecord: boolean;
    public canDelete: boolean | undefined | null;
    public canInsert: boolean | undefined | null;
    public formCanRead: boolean;
    public actualObjectStandardRights: StandardOperationRights;
    public componentTree: ComponentTreeType;
    public rightsManager: RightsManagerType;
    public isEditing: boolean = false;
    public isChanged: boolean = false;
    public actualObjectHasWF?: boolean = true;
    public isEditable?: boolean = true;
    public editModeChanged: Event<IEditableFrame, boolean> = new Event<WorkflowEditor, boolean>();
    public validationManager: ValidationManager = new ValidationManager();
    //#region Properties
    //#endregion Properties

    //#region Constructors
    constructor(params: FormControlParams) {
        super(params);

        this._setValuesMap = StringUtils.parseInlineDictionary(this.ownerForm.setValues);
        this.masterAttr = this._getMasterAttrName();
        this.element.setAttribute("data-behaviour", "frame"); // Bez tohoto se mi nezavolá dispose po zavření formuláře (neměl by dodávat form converter?)
        this.objectName = this.element.getAttribute("data-objectname");
        this._wfEditorHostKey = UUID.generate();
        this.uuid = FrameSharedMethods.generateUuid(!!AppContext.instance.configuration.developmentMode, this.element.id);
    }

    public async validate(_checkRecommended?: boolean): Promise<boolean> {
        //this.actualObject.validate(); // není to zbytečné? neprovádí se to samé při uložení?
        return true;
    }

    public async deleteRecord(): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    public async editRecord(): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    public async newRecord(): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    public async saveRecords(_id?: string, _checkRecommended?: boolean, _checkPostponed?: boolean): Promise<SaveResultData[]> {
        if (await this._saveWorkflowObject())
            return null;

        return [{
            data: this.actualObject,
        }];
    }

    public async cancelChanges(): Promise<void> {
        // o vše se stará dispose, tady není nic potřeba dělat
    }

    //#endregion Constructors
    public async initialize(): Promise<void> {
        UIManager.instance.showLoader();
        try {
            const { workflowObject, status } = await this._getWFEditorParams();
            await this._initializeWorkflowEditor(workflowObject, status);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    public refreshData(_manualRefresh: boolean): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public refreshActualObject(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public waitForDataLoad(): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    public waitForPendingUpdates(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public waitForActualObjectSet(): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    //#region IDisposable
    public override dispose(): void {
        this._workflowConnector?.dispose();

        if (this._openedViaUrl) // volat dispose na actual objectu je možné pouze, když byla instance actual objectu vytvořena editorem samotným
            this.actualObject?.dispose();

        if (WorkflowManager.uiWorkflows[this._wfEditorHostKey])
            delete WorkflowManager.uiWorkflows[this._wfEditorHostKey];

        this.rollbackContext();

        super.dispose();
    }
    //#endregion IDisposable

    private async _getWFEditorParams(): Promise<{ readonly workflowObject: OWObject; readonly status?: string }> {
        const isStatusViewerEnabled = this.ownerForm.setValues?.includes(CONST_STATUS_VIEW_ENABLED);

        if (isStatusViewerEnabled) {
            const promiseGetWFStatus = this.ownerForm.masterObject.getValue("stav_workflow");
            const promiseGetWFDocument = this.ownerForm.masterObject.getValue("workflow_dokladu");
            const [wfStatusAttr, result] = await Promise.all([promiseGetWFStatus, promiseGetWFDocument]) as [IAttrValue, OWObject];

            return { workflowObject: result, status: AttrValueReader.read(wfStatusAttr) };
        }

        if (this.ownerForm.formConditionParamsObject == null) {
            this._openedViaUrl = true;
            const selectId = AppContext.instance.addressParams.get(CONST_SELECTID_PARAM_NAME); // TODO předávat přes konstruktor při registraci WorkflowEditoru!!

            if (selectId == null) {
                throw new Error("URL parameter 'select-id' not provided! Could not initialize WF Editor instance!");
            }

            return { workflowObject: await OWManager.getObject(selectId, CONST_SWORKFLOW_CLASS_NAME) };
        }

        return { workflowObject: this.ownerForm.formConditionParamsObject };
    }

    /**
     * Nastaví custom jméno formuláře jako "Workflow Designer - {identifikátor}"
     */
    private async _initFormTitle(sWorkflow: OWObject): Promise<void> {
        try {
            const attrName = await sWorkflow.getValue("Identifikator");
            const wfdTranslation = await Localization.getTextPromise("XG-709164"); // Workflow Designer
            this.ownerForm.customTitle = `${wfdTranslation} - ${attrName.toString()}`;
        } catch (error) {
            ErrorHandler.instance.handleError(error);
        }
    }

    /**
     * Informuje formulář o tom, že v jeho obsahu došlo k editaci.
     * @param isEdited Příznak indikující, zdali došlo k editaci.
    */
    private _setIsWfEdited = async (isEdited: boolean): Promise<void> => {
        this.isEditing = isEdited;
        this.isChanged = isEdited;

        if (isEdited) {
            await this.createContext();
        }
    };

    /**
     * Nainicializuje aplikaci WF editoru.
     * @param sWorkflow objekt workflow
     * @param wfStatus xml stavu wf
    */
    private _initializeWorkflowEditor = async (sWorkflow: OWObject, wfStatus?: string): Promise<void> => {
        this.initializationStarted = true;
        this.actualObject = sWorkflow;

        const definition = await sWorkflow.getValue(this.masterAttr);

        if (definition == null) {
            throw new Error("Could not load workflow definition!");
        }

        const wfEditorMode = this._getWfEditorMode(wfStatus, !StringUtils.compareCaseInsensitive(this.masterAttr, CONST_DEFINITION_CHANGED_ATTR_NAME));
        this._workflowConnector = new WorkflowEditorConnector(
            this._wfEditorHostKey,
            this.element,
            await xmlToWorkflow(AttrValueReader.read(definition)),
            this._saveWorkflowObject,
            this._reloadWorkflowDefinition,
            this._setIsWfEdited,
            wfEditorMode,
            wfStatus,
        );

        await this._initFormTitle(sWorkflow);

        this.isInitialized = true;
    };

    /**
     * Funkce sloužící k uložení workflow. Sama se stará o načtení nové definice.
     * @returns Přiznak informující zdali uložení bylo úspěšné či nikoliv.
     */
    private _saveWorkflowObject = async (): Promise<boolean> => {
        try {
            if (!this.isInitialized)
                return false;

            UIManager.instance.showLoader();

            const newWorkflowXml = await workflowToXml(WorkflowManager.uiWorkflows[this._wfEditorHostKey].workflow);

            await this.actualObject.setValue(CONST_DEFINITION_CHANGED_ATTR_NAME, newWorkflowXml);
            const resultMessages = await this.actualObject.applyUpdates();

            if (resultMessages.length > 0)
                UIManager.instance.showResultMessages(resultMessages);

            await this.saveContext();

            return true;
        } catch (error) {
            if (error instanceof EOWValidateException) {
                await SharedMethods.handleValidationErrors([this.actualObject], error, this);
                return false;
            } else {
                MessageWindow.showAlert(new StringBuilder(error.message));
                return false;
            }
        } finally {
            UIManager.instance.hideLoader();
        }
    };

    /**
     * Přenačte definici workflow ze serveru.
     */
    private _reloadWorkflowDefinition = async (): Promise<string> => {
        try {
            if (!this.isInitialized)
                throw new Error("Workflow editor is not yet initialized! Cannot readload the instance!");

            UIManager.instance.showLoader();
            await this.actualObject.reload();
            return AttrValueReader.read(await this.actualObject.getValue(this.masterAttr)) as string;
        } finally {
            UIManager.instance.hideLoader();
        }
    };

    /**
     * Vrací název master atributu. (Pokud se nepodaří najít, vrátí "definice_zmenena").
     * @returns Název master atributu.
     */
    private _getMasterAttrName = (): string => {
        const attrName = this._setValuesMap.get("AttrName");
        return attrName ? attrName : CONST_DEFINITION_CHANGED_ATTR_NAME;
    };

    /**
     * Zjišťuje mód, ve kterém se má editor WF spustit.
     * @param wfStatusXml Xml stavu workflow.
     * @param isReadOnly Indikuje, zdali je workflow pouzeke čtení.
     * @returns
     */
    private _getWfEditorMode = (wfStatusXml: string, isReadOnly: boolean): WorkflowEditorMode => {
        if (wfStatusXml)
            return WorkflowEditorMode.StatusViewer;
        return isReadOnly ? WorkflowEditorMode.Viewer : WorkflowEditorMode.Editor;
    };
}
